/**
 * The monitor's "Details" cell: `jobs.stats_json`, rendered.
 *
 * FIVE KINDS, ONE PER BRANCH OF `describeStats`, AND NONE OF THEM MAY EVER
 * RENDER `[object Object]`. That is the specific failure DOM suite 3 of 3
 * exists to prevent: the column is passed through AS STORED by the API
 * (adminRoutes.ts:206-222), four writers already put four different shapes in
 * it, and the default React rendering of an unrecognised object is a string
 * that tells an operator nothing at all. Every kind here ends in text, a
 * `<code>` or a pretty-printed `<details>`; nothing is interpolated raw.
 *
 * TWO STATUS SUBTLETIES ARE RENDERED, not merely tolerated:
 *   - `skipped` carries the **done** pill. `markJobSkipped` sets status 'done'
 *     (jobStore.ts:81-84) because a run that legitimately did nothing -- a
 *     feature flag off at consume time -- is not a failure, and painting it as
 *     one sends an operator hunting a bug that is not there.
 *   - `postponed` carries the **queued** pill. `markJobPostponed` leaves status
 *     'queued' (jobStore.ts:91-100) because the row is STILL WAITING; the
 *     fairness budget postpones a run, it does not abort it.
 * Both pills come from `jobStatusPill`, the same function the Status column
 * uses, so the two columns speak one vocabulary: "Übersprungen" can never end
 * up next to a pill reading "Fehler" because this file invented its own word.
 *
 * Every decision is in lib/jobStats.ts and lib/jobDisplay.ts and is pinned in
 * Node; what this file adds is only the arrangement of it.
 */
import { describeResult, describeStats, truncateRaw, type JobStatsView } from '../lib/jobStats';
import { formatTimestamp, jobStatusPill } from '../lib/jobDisplay';
import { t } from '../lib/i18n';
import { StatusPill } from './StatusPill';

function ResultDetails({ result }: { result: unknown }) {
  const view = describeResult(result);
  if (view.kind === 'absent') return null;
  return (
    <details className="job-stats-result">
      <summary>{t('jobs.stats.resultLabel')}</summary>
      {view.kind === 'omittedUnserializable' ? <p>{t('jobs.stats.result.unserializable')}</p> : null}
      {view.kind === 'omittedTooLarge'
        ? <p>{t('jobs.stats.result.tooLarge', { bytes: view.bytes })}</p>
        : null}
      {view.kind === 'json' ? <pre>{view.text}</pre> : null}
    </details>
  );
}

function RunStats({ view }: { view: Extract<JobStatsView, { kind: 'run' }> }) {
  return (
    <div className="job-stats job-stats-run">
      <span className="job-stats-counters">
        {t('jobs.stats.run', {
          sheets: view.sheetsCalls,
          ebay: view.ebayCalls,
          ms: view.durationMs,
        })}
      </span>
      {/* Only when it is NOT the ordinary case: a run that took one delivery
          and was never postponed says nothing, so the two badges below mean
          something whenever they appear. */}
      {view.attempt > 1
        ? <span className="job-stats-attempt">{t('jobs.stats.attempt', { attempt: view.attempt })}</span>
        : null}
      {view.postponements > 0
        ? <span className="job-stats-postponements">{t('jobs.stats.postponements', { count: view.postponements })}</span>
        : null}
      {'result' in view ? <ResultDetails result={view.result} /> : null}
    </div>
  );
}

export function JobStatsCell({ stats }: { stats: unknown }) {
  const view = describeStats(stats);
  switch (view.kind) {
    case 'none':
      return <span className="job-stats job-stats-none">{t('jobs.stats.none')}</span>;
    case 'run':
      return <RunStats view={view} />;
    case 'skipped': {
      const pill = jobStatusPill('done');
      return (
        <div className="job-stats job-stats-skipped">
          <StatusPill kind={pill.kind} label={pill.label} />
          <span>{t('jobs.stats.skipped', { reason: view.reason })}</span>
        </div>
      );
    }
    case 'postponed': {
      const pill = jobStatusPill('queued');
      return (
        <div className="job-stats job-stats-postponed">
          <StatusPill kind={pill.kind} label={pill.label} />
          <span>
            {t('jobs.stats.postponed', {
              until: formatTimestamp(view.postponedUntil),
              count: view.postponements,
              reason: view.reason,
            })}
          </span>
        </div>
      );
    }
    case 'raw':
      // The FULL value in `title`, the same split lib/jobDisplay.ts's
      // `truncateError` makes: a 4 kB unparseable blob must not push the table
      // off the screen, and must not be lost either.
      return (
        <code className="job-stats job-stats-raw" title={view.text}>
          {truncateRaw(view.text)}
        </code>
      );
  }
}
